import { getOrders } from '@/api/orders.api'
import type { Order } from '@/common/order.common'

/**
 * Every order matching the filters, across all pages of GET /orders. The server pages and reports
 * totalItems, so a single capped page would silently drop the rest (SIB-1073).
 */
export async function loadAllOrders(
  params: Omit<NonNullable<Parameters<typeof getOrders>[0]>, 'page' | 'pageSize'> = {},
  pageSize = 500
): Promise<Order[]> {
  // Keyed by id: an order submitted while paging shifts the createdAt-sorted pages and would
  // otherwise appear twice.
  const byId = new Map<number, Order>()
  let fetched = 0
  for (let page = 1; ; page++) {
    const res = await getOrders({ ...params, page, pageSize })
    const batch = Object.values(res.items ?? {}).flatMap(bySib => Object.values(bySib).flat())
    const known = byId.size
    batch.forEach(o => byId.set(o.id, o))
    fetched += batch.length
    // A page with no order we haven't seen (empty, or the server echoing a page) ends the
    // loop: without it, a full repeated page and no totalItems would request forever.
    const done =
      byId.size === known ||
      (res.totalItems != null ? fetched >= res.totalItems : batch.length < pageSize)
    if (done) return [...byId.values()]
  }
}
